import { useEffect, useState, useMemo } from "react";
import { TTargetTax } from "types/tax";
import { dollarFormatter } from "utils";
import { IoAddCircleOutline, IoCloseCircleOutline, IoAdd } from "react-icons/io5";
import Select from "react-select";
import Tax from "./tax";
import axios from "axios";
import { reorderTaxes } from "../../utils";
import { useSnackbarContext } from "components/snackbar";
import { useLineItemContext } from "containers/lineItems/context/LineItemContext";
import { SubtleButton } from "components/button";

type Props = {
  taxes: TTargetTax[];
  groupType: string;
  isEdit: boolean;
  priceType: "list" | "install" | "sale";
  total: { list: number; sale: number; install: number };
  adjustedTaxableTotal: { list: number; sale: number; install: number };
  orderedTaxes: TTargetTax[];
  allTaxes: TTargetTax[];
  addTaxGroup: (taxGroup: any) => void;
  fetchTaxGroups: () => Promise<any[]>;
  taxGroups: any[];
  setTaxGroups: (taxGroups: any[]) => void;
  isCombinedGroup: (g: any) => boolean;
};

const TaxTypeArea = (props: Props) => {
  const {
    taxes,
    groupType,
    isEdit,
    priceType,
    total,
    adjustedTaxableTotal,
    orderedTaxes,
    allTaxes,
    addTaxGroup,
    fetchTaxGroups,
    taxGroups,
    setTaxGroups,
    isCombinedGroup,
  } = props;

  const { setTaxTotal, setTaxes, setTaxHasBeenAdded, taxHasBeenAdded, hasExistingTaxes } = useLineItemContext();

  const [showAddSelect, setShowAddSelect] = useState(false);
  const [taxOptions, setTaxOptions] = useState<any[]>([]);
  const [collapsed, setCollapsed] = useState({ [groupType]: false });
  const [taxTypeSelected, setTaxTypeSelected] = useState("");
  const [selectedTaxGroup, setSelectedTaxGroup] = useState<any>(null);

  const typeStringCap = groupType.charAt(0).toUpperCase() + groupType.slice(1);

  const addTax = (selectedTax: any) => {
    const newTax: TTargetTax = {
      ...selectedTax,
      tax_id: selectedTax.id,
      tax_type: groupType,
      label: selectedTax.originalLabel,
    };

    const updatedTaxes = reorderTaxes([...allTaxes, newTax]);
    setTaxes(updatedTaxes);
    setShowAddSelect(false);
  };
  
  const removeTax = (taxId: string | number) => {
    const needle = String(taxId);
    setTaxes(prev =>
      reorderTaxes(
        prev.filter(t =>
          String(t.id) !== needle && String(t.tax_id ?? t.id) !== needle
        )
      )
    );
  };
  
  // Remove an entire group in ONE state update
  const removeTaxGroup = (taxGroup: any) => {
    if (!taxGroup) return;
    const idsToRemove = new Set(
      (taxGroup.taxes || []).map((t: any) => String(t.id)) // group uses master tax ids
    );
    setTaxes(prev =>
      reorderTaxes(
        prev.filter(t => !idsToRemove.has(String(t.tax_id ?? t.id)))
      )
    );
    // (optional) reset UI state
    setTaxHasBeenAdded(false);
    setSelectedTaxGroup(null);
    setShowAddSelect(false);
  };

  const getTaxes = async (type: string) => {
    const res = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/getType/${type}`
    );
    return res.data;
  };

  const fetchTaxOptions = async () => {
    try {
      const options = await getTaxes(groupType);
      setTaxOptions(options);
    } catch (error) {
      console.error(`Error fetching ${groupType} options:`, error);
    }
  };

  const handleAddClick = async () => {
    const willOpen = !showAddSelect;
    setShowAddSelect(willOpen);
    if (willOpen) {
      setTaxTypeSelected("");
      setTaxHasBeenAdded(false);
      setSelectedTaxGroup(null);
      if (taxOptions.length === 0) await fetchTaxOptions();
    }
  };

  // Get IDs of taxes that are already added for this group type
  const existingTaxIds = (taxes || []).map(tax => String(tax.tax_id));

  // Filter out options that are already selected
  const selectOptions = taxOptions
    .filter((option: any) => !existingTaxIds.includes(String(option.id)))
    .map((option: any) => {
      const rate = option[`${priceType}_rate`];
      const type = option[`${priceType}_type`];
      
      // Format the rate display
      let rateDisplay = '';
      if (type === 'percent') {
        rateDisplay = ` % (${parseFloat(rate).toFixed(0)}%)`;
      } else if (type === 'fixed') {
        rateDisplay = ` $ (${dollarFormatter(rate)})`;
      }

      const optionLabel = `${option.label}${rateDisplay}`;
      
      return {
        ...option,
        originalLabel: option.label,
        label: optionLabel,
        value: option.id,
      };
    });

  const groupTotal = taxes.reduce((acc, tax) => {
    const rate = tax[`${priceType}_rate`];
    const typeTotal = tax.tax_type === "tax" ? adjustedTaxableTotal : total;

    // typeTotal[priceType] is in cents, rate is in cents for fixed or percent value
    // Round each individual tax before accumulating to match backend/PDF behavior
    // Result should be in cents
    const taxAmount = tax[`${priceType}_type`] === "percent" 
      ? Math.round((rate / 100) * typeTotal[priceType])
      : Math.round(rate);
    
    return acc + taxAmount;
  }, 0);

  useEffect(() => {
    if (groupType === "tax") {
      setTaxTotal(groupTotal);
    }
  }, [groupType, groupTotal, setTaxTotal]);

  const appliedCombinedGroup = useMemo(() => {
    if (groupType !== "tax" || !taxGroups?.length) return null;
  
    const currentTaxIds = new Set(
      (allTaxes || [])
        .filter(t => t.tax_type === "tax")
        .map(t => String(t.tax_id ?? t.id))
    );
  
    return (
      taxGroups.find(
        (g: any) =>
          isCombinedGroup(g) &&
          (g.taxes || []).every((t: any) => currentTaxIds.has(String(t.id)))
      ) || null
    );
  }, [groupType, taxGroups, allTaxes]);

  const protectedIds = useMemo(
    () =>
      new Set(
        appliedCombinedGroup?.taxes?.map((t: any) => String(t.id)) ?? []
      ),
    [appliedCombinedGroup]
  );

  const isProtectedTax = (t: TTargetTax) =>
    groupType === "tax" && protectedIds.has(String(t.tax_id ?? t.id));
  
  const noneAvailable = (
    <div className="px-4 py-2 text-gray-500 flex items-center justify-between">
      <span>No {groupType}{groupType === "tax" ? "es" : "s"} available</span>
    </div>
  );

  const taxContent = (
    <> 
      <button 
        className="w-full text-white pl-4 pr-3 py-2 font-semibold rounded-t-lg flex items-center justify-between bg-amber-500"
        onClick={(e) => {
          e.preventDefault();
          setCollapsed(prev => ({ ...prev, [groupType]: !prev[groupType] }));
        }}
      >
        <div className="flex items-end space-x-2">
          {isEdit && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleAddClick();
              }}
              disabled={taxHasBeenAdded || hasExistingTaxes}
              className="bg-white rounded-full hover:scale-110 transition-all duration-100 text-amber-500 active:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <IoAdd size={26} />
            </button>
          )}
          <div>{typeStringCap}</div>
        </div>

        <div className="px-4 py-2 text-white font-semibold">
          {dollarFormatter(groupTotal)}
        </div>
      </button>

      {isEdit && showAddSelect && !taxHasBeenAdded && (
        <>
          {taxTypeSelected === "" && !taxHasBeenAdded ? (
            <div className="flex items-center select-none">
              <SubtleButton
                primary
                onClick={() => {
                  setTaxTypeSelected("individual");
                  setTaxHasBeenAdded(false);
                  setSelectedTaxGroup(null);
                }}
              >
                Add Individual Tax
              </SubtleButton>
              <SubtleButton
                primary
                onClick={() => setTaxTypeSelected("combined")}
              >
                Add Combined Tax Rate
              </SubtleButton>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200">
              <>
                {taxTypeSelected === "combined" ? (
                  <div className="flex-1 px-4 py-3">
                    <Select
                      className="flex-[0.5_0.5_20%]"
                      options={taxGroups
                        .filter(isCombinedGroup)
                        .map(group => ({ label: group.label, value: group.id, ...group }))}
                      placeholder={taxTypeSelected === "combined" ? "Select a combined tax rate" : "Select a tax"}
                      onChange={(selectedOption: any) => {
                        if (selectedOption) {
                          addTaxGroup(selectedOption);
                          setTaxHasBeenAdded(true);
                          setSelectedTaxGroup(selectedOption);
                        }
                      }}
                      onMenuOpen={async () => {
                        if (taxGroups.length === 0) {
                          const groups = await fetchTaxGroups();
                          setTaxGroups(groups);
                        }
                      }}
                      formatOptionLabel={(option: any) => (
                        <div className="py-1">
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">
                            {option.taxes?.map((tax: any, index: number) => {
                              const rate = tax[`${priceType}_rate`];
                              const type = tax[`${priceType}_type`];
                              const rateDisplay = type === 'percent' 
                                ? `${parseFloat(rate).toFixed(0)}%` 
                                : dollarFormatter(rate);
                              
                              return (
                                <span key={tax.id} className="inline-flex items-center mr-3">
                                  <span className={`mr-1 font-semibold bg-white border w-4 h-4 rounded text-xs flex items-center justify-center ${
                                    type === 'percent' ? 'text-blue-500' : 'text-green-500'
                                  }`}>
                                    {type === 'percent' ? '%' : '$'}
                                  </span>
                                  <span>{tax.label}: {rateDisplay}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                ) : (
                  <div className="flex-1 px-4 py-3">
                    <Select
                      options={selectOptions}
                      placeholder={`Select a ${groupType}`}
                      onChange={(selectedOption: any) => {
                        if (selectedOption) {
                          addTax(selectedOption);
                          setTaxHasBeenAdded(true); 
                        }
                      }}
                      formatOptionLabel={(option: any) => (
                        <div className="flex items-center">
                          <span className={`-ml-2 mr-1 font-semibold bg-white border w-6 h-6 rounded-md flex items-center justify-center ${
                            option[`${priceType}_type`] === 'percent' ? 'text-blue-500' : 'text-green-500'
                          }`}>
                            {option[`${priceType}_type`] === 'percent' ? '%' : '$'}
                          </span>
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                )}
                <button 
                  onClick={() => {
                    setTaxTypeSelected("");
                    setTaxHasBeenAdded(false);
                    setShowAddSelect(false);
                  }}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <IoCloseCircleOutline size={20} />
                </button>
              </>
            </div>
          )}
        </>
      )}

      {appliedCombinedGroup && (
        <div className="flex items-center justify-between py-2 border-b bg-gray-100 pl-3 pr-1">
          <div className="text-md text-slate-600 font-semibold">
            {appliedCombinedGroup.label}
          </div>
          {isEdit && (
            <button 
              onClick={() => removeTaxGroup(appliedCombinedGroup)} 
              className="text-red-500 hover:text-red-700 rounded-full"
            >
              <IoCloseCircleOutline size={20} />
            </button>
          )}
        </div>
      )}

      {taxes && taxes.length > 0 && taxes.map((tax, i) => (
        <Tax
          priceType={priceType}
          total={total}
          taxableTotal={tax.tax_type === "tax" ? adjustedTaxableTotal : total}
          key={tax.id}
          data={tax}
          removeTax={() => removeTax(tax.id)}
          isEdit={isEdit} 
          isGroup={isProtectedTax(tax)}
          selectedTaxGroup={selectedTaxGroup}
        />
      ))}

      {((taxes && taxes.length === 0) && !showAddSelect) && noneAvailable}
    </>
  );

  const feeAndDiscountContent = (
    <>
      <button 
        className={`w-full text-white pl-4 pr-3 py-2 font-semibold rounded-t-lg flex items-center justify-between
          ${groupType === "discount" ? "bg-green-500" : ""}
          ${groupType === "fee" ? "bg-rose-500" : ""}
        `}
        onClick={(e) => {
          e.preventDefault();
          setCollapsed(prev => ({ ...prev, [groupType]: !prev[groupType] }));
        }}
      >
        <div className="flex items-end space-x-2">
          {isEdit && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleAddClick();
              }}
              className={`bg-white rounded-full hover:scale-110 transition-all duration-100
                ${groupType === "discount" ? 
                  "text-green-500 active:bg-green-200" 
                  : groupType === "fee" ? "text-rose-500 active:bg-rose-200" 
                  : ""
              }`}
            >
              <IoAdd size={26} />
            </button>
          )}
          <div>{typeStringCap}</div>
        </div>

        <div className="px-4 py-2 text-white font-semibold">
          {dollarFormatter(groupTotal)}
        </div>
      </button>
      
      {isEdit && showAddSelect && (
        <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200">
          <div className="flex-1 px-4 py-3">
            <Select
              options={selectOptions}
              placeholder={`Select a ${groupType}`}
              onChange={(selectedOption: any) => {
                if (selectedOption) {
                  addTax(selectedOption); 
                }
              }}
              formatOptionLabel={(option: any) => (
                <div className="flex items-center">
                  <span className={`-ml-2 mr-1 font-semibold bg-white border w-6 h-6 rounded-md flex items-center justify-center ${
                    option[`${priceType}_type`] === 'percent' ? 'text-blue-500' : 'text-green-500'
                  }`}>
                    {option[`${priceType}_type`] === 'percent' ? '%' : '$'}
                  </span>
                  <span>{option.label}</span>
                </div>
              )}
            />
          </div>
          <button 
            onClick={() => setShowAddSelect(false)}
            className="text-red-500 hover:text-red-700 transition-colors"
          >
            <IoCloseCircleOutline size={20} />
          </button>
        </div>
      )}

      {taxes && taxes.length > 0 && taxes.map((tax, i) => (
        <Tax
          priceType={priceType}
          total={total}
          taxableTotal={tax.tax_type === "tax" ? adjustedTaxableTotal : total}
          key={tax.id}
          data={tax}
          removeTax={() => removeTax(tax.id)}
          isEdit={isEdit} 
        />
      ))}

      {((taxes && taxes.length === 0) && !showAddSelect) && noneAvailable}
    </>
  );

  return (
    <>
      {groupType === "tax" ? taxContent : feeAndDiscountContent}
    </>
  )
};

export default TaxTypeArea;